/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useIntl, Link } from 'gatsby-plugin-intl';
import { navigate } from 'gatsby';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import parse from 'html-react-parser';

import { useFeatureFlag } from '../FeatureFlag';
import { getLunrIndex, filteredLunrSearch } from '../../utils/searchUtils';

import SearchBar from './SearchBar';

export const PredictiveSearchContainer = styled.ul`
  border-radius: 5px;
  background-color: ${themeGet('colors.white')};
  box-shadow: 0 2px 16px 0 rgba(43, 44, 50, 0.28);
  width: 100%;
  max-width: 500px;
  z-index: 10;

  position: absolute;
  top: 115%;
  right: 0;

  &.centered-results {
    right: auto;
    left: 50%;
    transform: translateX(-50%);
  }

  li {
    margin-bottom: 0;
    a,
    button {
      color: ${themeGet('colors.black')};
      font-size: 14px;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.29;
      letter-spacing: normal;
      display: inline-block;
      text-align: left;
      width: 100%;
      padding: 10px 26px;

      &:focus,
      &:hover {
        opacity: 1;
        color: ${themeGet('colors.red')};
        text-decoration: underline;
      }

      & > svg {
        display: none;
      }

      span {
        padding-bottom: 0;
        border-bottom: none;
        font-weight: inherit;
      }
    }

    &:lang(fr) {
      a,
      button {
        padding: 10px 18px;
      }
    }

    &:first-child,
    &:last-child {
      a,
      button {
        padding: 15px 26px;
      }

      &:lang(fr) {
        a,
        button {
          padding: 15px 18px;
        }
      }
    }
  }
`;
interface GlobalSearchProps {
  id?: string;
  className?: string;
  predictiveSearchClass?: string;
  predictiveSearch?: boolean;
  type?: string;
  placeholder?: string;
}

const withGlobalSearch = <P extends GlobalSearchProps>(SearchInput: React.ComponentType<P>) => {
  const GlobalSearch = ({
    id,
    className,
    predictiveSearchClass,
    predictiveSearch,
    type,
    placeholder,
    ...props
  }: P) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const intl = useIntl();
    const omitBlog = !useFeatureFlag('BLOG');
    const maxCompletions = 5;

    const formSubmit = (e: React.FormEvent, search: string = query) => {
      e.preventDefault();

      // We should probably setOpen(false) here as well, but that introduces other issues - eg the search x icon
      // is still displayed after the widget is closed.  Keeping existing behavior for now.
      if (search.length > 0) {
        navigate(`/${intl.locale}/search-results?searchQuery=${encodeURIComponent(search)}`);
      }
    };

    const focusOutsideOfSearch = useCallback((event: KeyboardEvent) => {
      if ((event.target as HTMLElement).classList.contains('searchable-marker')) {
        return;
      }
      setOpen(false);
      setQuery('');
    }, []);

    const clickOutsideOfSearch = useCallback((event: MouseEvent) => {
      if ((event.target as HTMLElement).classList.contains('searchable-marker')) {
        return;
      }
      setOpen(false);
      setQuery('');
    }, []);

    const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value.replace(/[&/\\#,+()$~%.'":*\-^<>{}]/g, ''));
    };

    useEffect(() => {
      window.addEventListener('keyup', focusOutsideOfSearch);
      window.addEventListener('click', clickOutsideOfSearch);
      return () => {
        window.removeEventListener('keyup', focusOutsideOfSearch);
        window.removeEventListener('click', clickOutsideOfSearch);
      };
    }, [focusOutsideOfSearch, clickOutsideOfSearch]);

    const results = useMemo(() => {
      const lunr = getLunrIndex(intl.locale);
      return filteredLunrSearch({ lunr, query, omitBlog });
    }, [query, intl.locale, omitBlog]);

    return (
      <SearchInput
        id={id}
        className={className}
        type={type}
        placeholder={placeholder}
        value={query}
        open={open}
        setOpen={setOpen}
        arialabel="Website search"
        predictiveSearch={predictiveSearch}
        onSubmit={formSubmit}
        onChange={handleQueryChange}
        {...props as P}
      >
        {predictiveSearch && open && (
          <PredictiveSearchContainer
            className={[
              'no-bullets',
              predictiveSearchClass,
              'searchable-marker',
            ].join(' ')}
          >
            {results
              .slice(0, maxCompletions)
              .map(({ id: nodeId, page, url }) => (
                <li key={nodeId}>
                  <Link
                    className="searchable-marker"
                    to={url}
                    state={{ fromFeed: true }}
                    onClick={() => {
                      setOpen(false);
                      setQuery('');
                    }}
                  >
                    {parse(page)}
                  </Link>
                </li>
              ))}
          </PredictiveSearchContainer>
        )}
      </SearchInput>
    );
  };

  return GlobalSearch;
};

export default withGlobalSearch(SearchBar);
